import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useGetClientSafetyGuard } from '../../useGetClientSafetyGuard';
import { VisitorDrawerState, visitorDrawerStateAtom, visitorMeasuresAtom } from '../state';

/**
 * Utility hook encapsulating shared logic for measure drawer controls.
 *
 * @returns An object containing the necessary props for the measure drawer control.
 * @property helperText - The text to display in the tooltip or menu item.
 * @property isDrawerOpen - Whether the measures drawer is already open.
 * @property onClick - The function to call when the drawer control is clicked.
 * @property outstandingMeasuresCount - The number of outstanding (incomplete)
 * measures that the client has.
 * @property shouldShowButton - Whether the button should be shown.
 * @property showVisualIndicator - Whether the visual indicator should be shown
 * on the control button.
 */
export function useMeasureDrawerControl() {
  const { userClicked } = useTrackEvent();
  const { patientShortId = '' } = useParams();
  const t = useTranslate();

  const { requiresSafetyGuards } = useGetClientSafetyGuard();
  const shouldShowButton = requiresSafetyGuards === false;

  const [drawerState, setDrawerState] = useAtom(visitorDrawerStateAtom);
  const isDrawerOpen = drawerState === VisitorDrawerState.PENDING_FORMS;

  const visitorMeasures = useAtomValue(visitorMeasuresAtom);
  const outstandingMeasuresCount = visitorMeasures?.length ?? 0;
  const showVisualIndicator = outstandingMeasuresCount > 0;
  const helperText = isDrawerOpen
    ? t('telehealth-core:controls.closeMeasuresOutstandingCount', { count: outstandingMeasuresCount })
    : t('telehealth-core:controls.openMeasuresOutstandingCount', { count: outstandingMeasuresCount });

  const onClick = useCallback(() => {
    setDrawerState((prev) => (prev === VisitorDrawerState.PENDING_FORMS ? null : VisitorDrawerState.PENDING_FORMS));
    userClicked(EventTypeEnum.CLIENT_MEASURES_CLICKED, {
      patientShortId,
      outstandingMeasureTypes: visitorMeasures?.map((m) => m.schema.type) ?? [],
    });
  }, [patientShortId, setDrawerState, userClicked, visitorMeasures]);

  return {
    helperText,
    isDrawerOpen,
    onClick,
    outstandingMeasuresCount,
    shouldShowButton,
    showVisualIndicator,
  };
}
